"use client"

import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { collection, query, where, getDocs, updateDoc, doc, increment } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { Loader2, Share2, Clock, MapPin, ChevronLeft, Facebook, Twitter, Linkedin, Link as LinkIcon, Calendar } from "lucide-react"
import BackButton from "@/components/ui/back-button"

export default function ArticlePage() {
  const { slug } = useParams()
  const [article, setArticle] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [readingProgress, setReadingProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = (window.scrollY / totalHeight) * 100
      setReadingProgress(progress)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const q = query(collection(db, "articles"), where("slug", "==", slug))
        const snapshot = await getDocs(q)
        let docData = null
        let docId = null

        if (!snapshot.empty) {
          docData = snapshot.docs[0].data()
          docId = snapshot.docs[0].id
        } else {
          // Fallback: try lookup by ID
          try {
            const docSnap = await getDoc(doc(db, "articles", slug as string))
            if (docSnap.exists()) {
              docData = docSnap.data()
              docId = docSnap.id
            }
          } catch (e) {
            // Malformed ID
          }
        }

        if (docData) {
          setArticle({ id: docId, ...docData })
          await updateDoc(doc(db, "articles", docId as string), { views: increment(1) }).catch(() => {})
        }
      } catch (error) {
        console.error("Error:", error)
      } finally {
        setIsLoading(false)
      }
    }
    if (slug) fetchArticle()
  }, [slug])

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-[#b48c36] animate-spin" />
      </div>
    )
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6 text-center">
        <h2 className="text-2xl font-serif text-slate-950 mb-4">Article Not Found</h2>
        <Link to="/current-affairs" className="text-[#b48c36] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
           <ChevronLeft size={16} /> Back to Journal
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1.5 z-50 bg-slate-50">
         <div 
           className="h-full bg-[#b48c36] shadow-[0_0_10px_rgba(180,140,54,0.4)] transition-all duration-100" 
           style={{ width: `${readingProgress}%` }}
         />
      </div>

      <main className="max-w-[1200px] mx-auto px-6 pt-12 lg:pt-20">
         <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            
            {/* Handy Sidebar - Left Aligned */}
            <aside className="lg:w-[300px] space-y-10 lg:sticky lg:top-24 h-fit">
               <BackButton label="Internal Journal" className="mb-4" />

               <div className="space-y-6">
                  <div className="bg-[#b48c36] text-white text-[9px] font-bold px-3 py-1 rounded w-fit uppercase tracking-widest">
                     {article.category}
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-serif font-bold text-slate-950 leading-[1.2] tracking-tight">
                     {article.title}
                  </h1>
               </div>

               <div className="pt-8 border-t border-slate-100 space-y-6">
                  <div className="flex items-center gap-3">
                     <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 overflow-hidden">
                        <div className="bg-slate-900 w-full h-full flex items-center justify-center text-white font-serif italic text-xs">R</div>
                     </div>
                     <div>
                        <p className="text-[11px] font-bold text-slate-950 uppercase tracking-wide">Rohith's Editorial</p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest">Team HIAS Analysis</p>
                     </div>
                  </div>

                  <div className="space-y-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">
                     <div className="flex items-center gap-2 bg-slate-50 p-2 rounded"><Clock size={14} className="text-[#b48c36]"/> {Math.max(2, Math.ceil((article.content?.length || 0) / 1000))} Min Read</div>
                     <div className="flex items-center gap-2 bg-slate-50 p-2 rounded"><Calendar size={14} className="text-[#b48c36]"/> {article.createdAt?.toDate ? new Date(article.createdAt.toDate()).toLocaleDateString() : 'Recent'}</div>
                  </div>
               </div>

               <div className="pt-8 border-t border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Share Insights</p>
                  <div className="flex items-center gap-4 text-slate-400">
                     <button className="w-10 h-10 rounded-full border border-slate-100 flex items-center justify-center hover:text-[#b48c36] hover:border-[#b48c36] transition-all"><Twitter size={16} /></button>
                     <button className="w-10 h-10 rounded-full border border-slate-100 flex items-center justify-center hover:text-[#b48c36] hover:border-[#b48c36] transition-all"><Linkedin size={16} /></button>
                     <button className="w-10 h-10 rounded-full border border-slate-100 flex items-center justify-center hover:text-[#b48c36] hover:border-[#b48c36] transition-all"><LinkIcon size={16} /></button>
                  </div>
               </div>
            </aside>

            {/* Main Content - Right Aligned */}
            <div className="flex-1 max-w-[750px]">
               {article.image && (
                  <div className="mb-12 overflow-hidden rounded-2xl shadow-2xl shadow-slate-200/50 border border-slate-100">
                     <img 
                       src={article.image} 
                       className="w-full max-h-[420px] object-cover" 
                       alt="" 
                     />
                  </div>
               )}

               <div className="prose prose-slate max-w-none pt-4">
                  <div className="font-serif text-slate-800 leading-[1.8] text-lg whitespace-pre-wrap tracking-wide antialiased transition-all">
                     {article.content}
                  </div>
               </div>


            </div>

         </div>
      </main>
    </div>
  )
}
